<template>
  <div class="calendar-wrapper">
    <div class="selected-date-panel">
      <div v-if="selectedDate" class="date-display">
        <div class="weekday">{{ formattedDate.weekday }}</div>
        <div class="day">{{ formattedDate.day }}</div>

        <div class="file-info">
          <!-- Loading state -->
          <div
            v-if="selectedDay.fileName === 'Cargando...'"
            class="loading-skeleton"
          >
            <div class="skeleton-line"></div>
            <div class="skeleton-line"></div>
            <div class="skeleton-line"></div>
          </div>
          <template v-else>
            <div class="file-meta">
              <span class="label">Subido por:</span>
              {{ selectedDay.createdBy || "No disponible" }}
            </div>
            <div class="file-meta">
              <span class="label">Fecha de subida:</span>
              {{
                selectedDay.createdAt
                  ? formatDateTime(selectedDay.createdAt)
                  : "No disponible"
              }}
            </div>
            <div class="file-meta">
              <span class="label">Archivo:</span>
              {{ selectedDay.fileName || "No disponible" }}
            </div>
            <div v-if="selectedDay.status !== null" class="file-meta">
              <span class="label">Estado:</span>
              {{ getTranslatedStatus(selectedDay.status) }}
            </div>
          </template>
        </div>
      </div>

      <div v-else class="date-displace-placeholder">Seleccione una fecha</div>
    </div>

    <div class="calendar-container">
      <VCalendar
        :attributes="attributes"
        expanded
        :rows="1"
        class="full-width-calendar"
        locale="es"
        :masks="{ title: 'MMMM YYYY', weekdays: 'WW' }"
        @did-move="handleCalendarMove"
        @dayclick="handleDayClick"
        :trim-weeks="true"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { FileManagerService } from "@/services/fileManagerService";
import { useFileManager } from "@/composables/useFileManager";
import { getTranslatedStatus } from "@/utils//statusTranslations";
import { useDateFormatter } from "@/composables/useDateFormatter";

const selectedDate = ref(null);

const selectedDay = ref({
  date: null,
  fileName: null,
  status: null,
  createdAt: null,
  createdBy: null,
});

const { filesData, currentPeriod, fetchDates, attributes } = useFileManager();

const { formattedDate, formatDateTime } = useDateFormatter(selectedDate);

const handleCalendarMove = (pages) => {
  const page = pages[0];
  if (page?.month !== undefined && page?.year !== undefined) {
    currentPeriod.value = {
      month: page.month,
      year: page.year,
    };
    fetchDates(1);
  }
};

const handleDayClick = async (day) => {
  try {
    selectedDate.value = day.date;

    const selectedDateObj = new Date(day.date);
    const fileData = filesData.value.find(
      (f) => new Date(f.date).toDateString() === selectedDateObj.toDateString()
    );

    selectedDay.value = {
      date: day.date,
      fileName: "Cargando...",
      status: null,
      createdAt: null,
      createdBy: null,
    };

    if (fileData?.id) {
      const response = await FileManagerService.getFile(fileData.id);
      console.log("response", response);
      selectedDay.value = {
        date: day.date,
        fileName: response.data.filename,
        status: response.data.status,
        createdAt: response.data.created_at,
        createdBy: response.data.user.name,
      };
    } else {
      selectedDay.value = {
        date: day.date,
        fileName: "Sin archivo",
        status: null,
        createdAt: null,
        createdBy: null,
      };
    }
  } catch (error) {
    selectedDay.value = {
      date: day.date,
      fileName: "Error al cargar",
      status: 3,
      createdAt: null,
      createdBy: null,
    };
  }
};

onMounted(() => fetchDates(1));
</script>


<style scoped>
.calendar-wrapper {
  display: grid;
  grid-template-columns: 30% 70%;
  min-height: 400px;
}

.selected-date-panel {
  box-sizing: border-box;
  background: #0078d4;
  padding: 2rem;
  border-right: 2px solid #eee;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  height: 100%;
}


.selected-date-panel,
.calendar-container {
  min-width: 0;
}

.date-display {
  text-align: center;
  margin-top: 2rem;
}

.weekday {
  font-size: 2rem;
  color: white;
  text-transform: capitalize;
  margin-bottom: 0.5rem;
}

.day {
  font-size: 3rem;
  font-weight: 700;
  color: white;
}

.date-displace-placeholder {
  color: #ffffff;
  font-style: italic;
  margin-top: 2rem;
}

.date-displace-placeholder {
  color: white;
  font-style: italic;
}

.calendar-container {
  width: 100%;
  max-width: 100%;
}

:deep(.vc-container) {
  width: 100% !important;
  border: none !important;
  background: #f1f1f1;
}

:deep(.vc-weeks) {
  width: 100%;
  padding: 0 3%;
}

:deep(.vc-day) {
  flex: 1 0 calc(14.28% - 4px);
  min-width: 120px;
  min-height: 120px;
  margin: 2px;
  border: 1px solid #eee;
}

.selected-day-panel {
  width: 300px;
  padding: 1rem;
  border-left: 2px solid #eee;
  min-height: 400px;
}

.day-info h3 {
  color: #2c3e50;
  border-bottom: 2px solid #3498db;
  padding-bottom: 0.5rem;
}

.file-info {
  margin-top: 1rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  min-height: 80px;
}

.file-meta {
  font-size: 0.9rem;
  margin: 0.5rem 0;
  color: #495057;
}

.label {
  font-weight: 600;
  color: #2c3e50;
  margin-right: 0.5rem;
}

.vc-container {
  --vc-font-family: "Roboto", sans-serif;
  --vc-weekday-color: #2c3e50;
  --vc-day-content-color: #34495e;
}

.vc-title {
  color: blue;
  font-size: 1.5rem !important;
}

:deep(.vc-day) {
  padding: 4px;
  border: none !important;
  margin: 0 !important;
  min-width: unset !important;
  min-height: unset !important;
}

:deep(.vc-weekdays) {
  border-bottom: none !important;
}

:deep(.vc-weeks) {
  border: none !important;
  gap: 4px;
}

:deep(.vc-week, .vc-weekdays) {
  margin-bottom: 5px;
}

:deep(.vc-weekday) {
  margin: 10px 5px;
  color: black;
  font-size: 17px;
}

:deep(.vc-day:not(.is-disabled)) .vc-day-content {
    font-size: 20px;
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
  width: 100% !important;
  aspect-ratio: 1 / 1 !important;
  margin: auto !important;
  border-radius: 50% !important;
  background-color: #3498db !important;
  color: white !important;
  font-weight: 500 !important;
  transition: all 0.2s ease !important;
  height: auto !important;
}

:deep(.vc-header .vc-title) {
  color: #0078d4 !important;
}

:deep(.vc-day:not(.is-disabled):hover .vc-day-content) {
  background-color: #2980b9 !important;
  transform: scale(1.1) !important;
}

.file-info {
  text-align: left;
  margin-top: 1rem;
  width: 100%;
}

.file-meta {
  margin: 0.5rem 0;
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
}

.label {
  font-weight: 600;
  min-width: 100px;
  color: #2c3e50;
}

.loading-skeleton {
  width: 100%;
  padding: 0.5rem 0;
}

.skeleton-line {
  height: 1rem;
  background: #e0e0e0;
  border-radius: 4px;
  margin: 0.5rem 0;
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0% { opacity: 1; }
  50% { opacity: 0.5; }
  100% { opacity: 1; }
}

.skeleton-line:nth-child(1) { width: 70%; }
.skeleton-line:nth-child(2) { width: 80%; }
.skeleton-line:nth-child(3) { width: 60%; }


@media (max-width: 768px) {
  .calendar-wrapper {
    grid-template-columns: 1fr;
    grid-template-rows: auto auto;
    gap: 1rem;
  }

  .selected-date-panel {
    padding: 1rem;
  }

  .calendar-container {
    margin-top: 1rem;
  }
}

</style>